import { createAsyncThunk } from "@reduxjs/toolkit";
import { format, isBefore, startOfDay, subDays } from "date-fns";
import { isEmpty, keys } from "lodash";
import PartnerApi from "utility/api";
import { checkIsAdmin } from "utility/helpers/user";

export const fetchOrdersRequest = createAsyncThunk(
  "order/fetchOrdersRequest",
  async (page = 1, { getState, rejectWithValue }) => {
    try {
      const { order } = getState();

      const isAdmin = await checkIsAdmin();
      const archiveLimit = subDays(startOfDay(new Date()), 7);

      let filters = { ...order.filters };
      if (isAdmin && (!filters.start || isBefore(new Date(filters.start), archiveLimit))) {
        filters = { ...filters, start: new Date(archiveLimit).toISOString() };
      }

      const result = await PartnerApi.orders({ page, filters });
      return result;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const fetchFilteredOrdersRequest = createAsyncThunk(
  "order/fetchFilteredOrdersRequest",
  async (filters, { getState, rejectWithValue }) => {
    const { order } = getState();

    let currFilters = { ...filters };

    if (currFilters.status === "all") currFilters.status = null;
    if (currFilters.paymentStatus === "all") currFilters.paymentStatus = null;
    currFilters = keys(currFilters).reduce((prev, field) => {
      if (isEmpty(currFilters[field])) return prev;
      return { ...prev, [field]: currFilters[field] };
    }, {});

    const isAdmin = await checkIsAdmin();
    const archiveLimit = subDays(startOfDay(new Date()), 7);

    if (isAdmin && (!currFilters.start || isBefore(new Date(currFilters.start), archiveLimit))) {
      currFilters = { ...currFilters, start: new Date(archiveLimit).toISOString() };
    }

    try {
      const result = await PartnerApi.orders({ page: order.pagination.active_page, filters: currFilters });
      return result;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const fetchOrderPositionRequest = createAsyncThunk(
  "order/fetchOrderPositionRequest",
  async (id, { rejectWithValue }) => {
    try {
      const result = await PartnerApi.riderPosition(id);
      return result;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const cancelOrderRequest = createAsyncThunk("order/cancelOrderRequest", async (id, { rejectWithValue }) => {
  try {
    const result = await PartnerApi.cancelOrder(id);
    return result;
  } catch (error) {
    throw rejectWithValue(error);
  }
});

export const cancelOrderBatchRequest = createAsyncThunk(
  "order/cancelOrderBatchRequest",
  async (ids, { rejectWithValue }) => {
    try {
      const result = await PartnerApi.cancelOrderBatch(ids);
      return result;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const downloadOrdersReportRequest = createAsyncThunk(
  "order/downloadOrdersReportRequest",
  async (filters, { getState, rejectWithValue }) => {
    try {
      const {
        contact: { profile },
      } = getState();

      const blob = await PartnerApi.downloadOrderReport({ userId: profile.contact_id, filters });

      const filename = `orders-${profile.lastName}_${format(new Date(filters.start), "dd-MM-yyyy")}_${format(
        new Date(filters.end),
        "dd-MM-yyyy"
      )}.csv`;

      // Ensure proper MIME (some servers omit it)
      const csvBlob = blob.type ? blob : new Blob([blob], { type: "text/csv;charset=utf-8" });

      const url = URL.createObjectURL(csvBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename; // critical to force download
      a.rel = "noopener"; // safety
      document.body.appendChild(a); // Safari needs it in DOM sometimes
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);

      return true;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);
